import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import { usePlayer } from "@/lib/usePlayer";
import { useUpdatePlayerGame, useChangePlayerGame } from "@/lib/usePlayerGame";
import toast from "react-hot-toast";
import Page from "@/components/layout/Page";
import Summary from "@/components/endless/Summary";
import ErrorState from "@/components/endless/ErrorState";
import UserPrompt from "@/components/endless/UserPrompt";
import Loading from "@/components/common/Loading";
import DailyQuestion from "@/components/daily/DailyQuestion";
import PlayShareDialog from '@/components/common/PlayShareDialog';
import Error from "@/pages/_error";
import { getPublicConfig } from '@/pages/api/config';
import { historyToPieMask, isTruthy, sendGtagEvent } from "@/lib/utils";
import {
	GAME_MODES,
	ENDLESS_USER_ACTIONS,
	TOAST_DURATION,
	ENDLESS_TIMER_LIMIT,
	QUESTIONS_PER_RUN,
	PLAYLIST_DETAILS
} from "@/lib/constants";
import { FEEDBACK, ENDLESS_GAME_STATES } from "@hasbro/tpi-data-models";

const CLOCK_TICK = 0;
const timerLimit = ENDLESS_TIMER_LIMIT;
const playListDetails = PLAYLIST_DETAILS['endless'];
const infiniteEnabled = isTruthy(getPublicConfig('infiniteEnabled'))
const PLAYER_UI_URL = getPublicConfig('PLAYER_UI_URL');

// Switched page to SSR so that path params and query params can be used in the first render.
export const getServerSideProps = async () => {
	return { props: { } } // yes, this is empty
}

const Infinite = () => {
	const queryClient = useQueryClient();
	const router = useRouter();
	const { shareId } = router.query;
	const { data: player, isSuccess } = usePlayer();
	const { mutate: changePlayerGame } = useChangePlayerGame();
	const { mutateAsync: updatePlayerGame } = useUpdatePlayerGame();

	const [clock, setClock] = useState({isRunning: false})
	const [timeRemaining, setTimeRemaining] = useState(0)
	const interval = useRef()

	// wrap stopTimer in a useCallback hook to avoid unnecessary re-renders
	const stopTimer = useCallback(() => {
		clearInterval(interval.current)
		setClock({...clock, isRunning: false})
	}, [clock, interval])

	// temporarily set the state to 'None' while we await async game update
	// this will trigger the loading view and prevent previous/blank question
	const setLoading = () => {
		queryClient.setQueryData(['player'], { 
			...player,
			pursuit: {state: ENDLESS_GAME_STATES.None}
		});
	}

	// if URL has a shareId, make sure that pursuit is loaded
	useEffect(() => {
		if (shareId && isSuccess) {
			changePlayerGame({uid: player?.uid, body: {shareId}});
		}
  }, [isSuccess]);

	// initial visit state load
	useEffect(() => {
		const onPlay = async () => {
			setLoading();
			setTimeRemaining(timerLimit);
			await updatePlayerGame({
				uid: player?.uid, 
				body: {
					id: player?.pursuit?._id,
					action: ENDLESS_USER_ACTIONS.next,
			}})
			setClock({
				startTime: Date.now(),
				isRunning: true,
			});
		};
		if (player?.pursuit?.state === ENDLESS_GAME_STATES.Setup) {
			onPlay();
		}
		if(player?.pursuit?.state === ENDLESS_GAME_STATES.Question) {
			setTimeRemaining(timerLimit);
			setClock({
				startTime: Date.now(),
				isRunning: true,
			});

		}
	}, [player?.pursuit?.state]);
		
	// clock management
	useEffect(() => {
		if (clock?.isRunning) {
			interval.current = setInterval(() => {
					const elapsed = Date.now() - clock.startTime;
					const remaining = ENDLESS_TIMER_LIMIT - elapsed;
					setTimeRemaining(remaining);
				if (remaining <= 0) {
					stopTimer();
					onUserChoice('')
				}
			}, CLOCK_TICK);
		} else if (interval.current) {
			clearInterval(interval.current);
		}
		return () => {
			if (interval.current) clearInterval(interval.current);
		};
	}, [clock]);
	
	// register refresh and navigation handlers
	// make sure the clock gets set to 0 on reload
	useEffect(() => {
		const exitingFunction = () => {
			setClock({isRunning: true, startTime: Date.now() - (ENDLESS_TIMER_LIMIT + 100)})
		};
		// call exit function when we navigate away
		router.events.on("routeChangeStart", exitingFunction);
		// call exit function when we refresh
		window.onbeforeunload = exitingFunction;
	}, []);

	const onQuitClick = () => {
		sendGtagEvent({
			action: 'endless_quit_click',
			category: 'engagement',
			label: 'Endless Quit Click',
			value: 'click',
			user_id: player.uid
		});
		onQuit()
	}

	const onResetClick = () => {
		sendGtagEvent({
			action: 'endless_reset_click',
			category: 'engagement',
			label: 'Endless Reset Click',
			value: 'click',
			user_id: player?.uid
		});
		return onReset();
	}

	const onReset = async () => {
		setClock({...clock, isRunning: false});

		// I don't think we need this API call anymore - 
		// just clear the local game data to display the prompt form
	
		// await updatePlayerGame({
		// 	uid: player?.uid, 
		// 	body: {
		// 		id: player?.pursuit?._id,
		// 		action: ENDLESS_USER_ACTIONS.reset
		// 	}
		// })
		
		queryClient.setQueryData(['player'], { 
			...player,
			pursuit: null
		});
	}

	const onPlayAgain = async () => {
		await updatePlayerGame({
			uid: player?.uid, 
			body: {
				id: player?.pursuit?._id,
				action: ENDLESS_USER_ACTIONS.play_again
			}
		})

		sendGtagEvent({
			action: 'endless_play_again_click',
			category: 'engagement',
			label: 'Endless Play Again Click',
			value: 'click',
			user_id: player.uid
		});
	}

	const onUserChoice = useCallback(async choice => {
		if (clock.isRunning && player?.pursuit?.state === ENDLESS_GAME_STATES.Question){
			// stop time
			setClock({...clock, isRunning: false});

			await updatePlayerGame({
				uid: player?.uid, 
				body: {
					id: player?.pursuit?._id,
					action: ENDLESS_USER_ACTIONS.choice,
					choice,
					question: questionList[questionIndex]
			}})

			sendGtagEvent({
				action: 'endless_choice_click',
				category: 'engagement',
				label: 'Endless Choice Click',
				value: 'click',
				user_id: player.uid
			});
		}
	}, [player?.pursuit, clock])

	const onNext = async () => {
		// make sure the clock is stopped
		setClock({...clock, isRunning: false});

		setLoading()

		await updatePlayerGame({
			uid: player?.uid, 
			body: {
				id: player?.pursuit?._id,
				action: ENDLESS_USER_ACTIONS.next
			}
		});

		sendGtagEvent({
			action: 'endless_next_click',
			category: 'engagement',
			label: 'Endless Next Click',
			value: 'click',
			user_id: player.uid
		});
	}

	const onCancel = () => {
		stopTimer();
		router.push('/');
	}

	const onLoaderCancel = () => {
		sendGtagEvent({
			action: 'infinite_loader_cancel',
			category: 'engagement',
			label: 'Infinite Loader Cancel',
			value: 'click',
			user_id: player?.uid
		});
		onCancel()
	}

	const onQuit = async () => {
		stopTimer();

		await updatePlayerGame({
			uid: player?.uid, 
			body: {
				id: player?.pursuit?._id,
				action: ENDLESS_USER_ACTIONS.quit
			}
		})

		router.push('/')
	}

	const onQuestionFeedback = async (questionId, feedback) => {
		if (player?.pursuit) {
			const res = await fetch(`/api/user/${player.uid}/feedback`, {
				method: 'POST',
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					id: player?.pursuit._id,
					mode: GAME_MODES.endless,
					questionId,
					feedback,
				}),
			})

			const isFlagUp = feedback === FEEDBACK.liked;
			const questionText = player?.pursuit.history?.[index]?.question || undefined;
			const choices = player?.pursuit.history?.[index]?.choices || undefined;
			const answerText = player?.pursuit.history?.[index]?.answer || undefined;

			sendGtagEvent({
				action: isFlagUp ? 'endless_question_flag_up' : 'endless_question_flag_down',
				category: 'engagement',
				label: isFlagUp ? 'Endless Question Flag Up' : 'Endless Question Flag Down',
				value: 'click',
				question_text: questionText,
				answer_choices: choices,
				answer_text: answerText,
				feedback_text: feedback,
				user_id: player.uid,
			});

			if (res.ok) {
				const dat = await res.json()
				// setPursuit(dat)
			}
		}
	}

	const onPlaylistFeedback = async (playlistId, feedback) => {
		if (player?.pursuit) {
			await fetch(`/api/user/${player.uid}/playlistFeedback`, {
				method: 'POST',
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					id: playlistId,
					feedback,
					uid: player.uid
				}),
			})

			const isFlagUp = feedback === FEEDBACK.liked;

			sendGtagEvent({
				action: isFlagUp ? 'playlist_flag_up' : 'playlist_flag_down',
				category: 'engagement',
				label: isFlagUp ? 'Playlist Flag Up' : 'Playlist Flag Down',
				value: 'click',
				feedback_text: isFlagUp ? 'liked' : 'disliked',
				playlist_id: playlistId,
				user_id: player.uid,
			});
		}
	}

	const onPromptSubmit = async (prompt, captchaToken) => {
		// temporarily remove the pursuit data to render the "loading" view
		setLoading();
		const topic = prompt.prompt.trim();

		await updatePlayerGame({
			uid: player?.uid, 
			body: {
				id: player?.pursuit?._id,
				topic
			}, 
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				...(captchaToken && { "x-captcha-token": captchaToken })
		}})

		sendGtagEvent({
			action: 'endless_prompt_submit',
			category: 'engagement',
			label: 'Endless Prompt Submit',
			value: 'click',
			topic,
			user_id: player.uid
		});


		setTimeRemaining(timerLimit);
	}

	/** For custom_share, trending, and staff_pick playlists */
	const onShare = (event) => {
		event.stopPropagation();
    if (!player?.pursuit) return;

    sendGtagEvent({
      action: "infinite_pursuit_share",
      category: "engagement",
      label: "Infinite Pursuit Share",
      value: "click",
      user_id: player.uid,
      topic: player?.pursuit.playlist.topic,
      type: player?.pursuit.type,
    });

    const playlistTopicTitle =
		player?.pursuit.type === "custom_share"
        ? "Topic"
        : player?.pursuit.type === "staff_pick"
          ? "Staff Picks"
          : player?.pursuit.type === "trending"
            ? "Trending Playlist"
            : "Topic";

    const emojiString = player?.pursuit.playlist.questions
      .map((question, idx) => (question.answer === player?.pursuit.playerChoices?.[idx] ? "✅" : "❌"))
      .join("");

    const content = {
      text: `Trivial Pursuit Infinite
${playlistTopicTitle}: "${player?.pursuit.playlist.topic}"
Score: ${player?.pursuit.playerScore}/${QUESTIONS_PER_RUN}▼
${emojiString}`,
      url: `${PLAYER_UI_URL}/infinite?shareId=${player?.pursuit.shareId}&utm_source=tpi-game&utm_medium=share`,
    };
    if (navigator?.share && navigator.canShare(content) && navigator.maxTouchPoints) {
      navigator.share(content);
    } else {
      // desktop/alternate sharing. copy to clipboard
      navigator.clipboard.writeText(`${content.text}\n${content.url}`).then(
        () => {
          // success
          toast.success("Copied to clipboard 👍", { duration: TOAST_DURATION });
        },
        () => {
          // failure
          toast.error("Hmm. Could not Copy to clipboard");
        }
      );
    }
    return true;
	}
	
	/** For custom playlists */
	const onChallenge = (event) => {
		event.stopPropagation();
    if (!player?.pursuit) return;

    sendGtagEvent({
      action: "infinite_pursuit_challenge",
      category: "engagement",
      label: "Infinite Pursuit Challenge",
      value: "click",
      user_id: player.uid,
      topic: player?.pursuit.playlist.topic,
      type: player?.pursuit.type,
    });

		const emojiString = player?.pursuit.playlist.questions
      .map((question, idx) => (question.answer === player?.pursuit.playerChoices?.[idx] ? "✅" : "❌"))
      .join("");

    const content = {
      text: `Trivial Pursuit Infinite
My Trivia Playlist: "${player?.pursuit.playlist.topic}"
Score: ${player?.pursuit.playerScore}/${QUESTIONS_PER_RUN}▼
${emojiString}
Think you can beat me?`,
      url: `${PLAYER_UI_URL}/infinite?shareId=${player?.pursuit.shareId}&utm_source=tpi-game&utm_medium=share`,
    };
    if (navigator?.share && navigator.canShare(content) && navigator.maxTouchPoints) {
      navigator.share(content);
    } else {
      // desktop/alternate sharing. copy to clipboard
      navigator.clipboard.writeText(`${content.text}\n${content.url}`).then(
        () => {
          // success
          toast.success("Copied to clipboard 👍", { duration: TOAST_DURATION });
        },
        () => {
          // failure
          toast.error("Hmm. Could not Copy to clipboard");
        }
      );
    }
    return true;
	}

	const onSharePlaylistPlay = async (event) => {
		await updatePlayerGame({
			uid: player?.uid, 
			body: {
				id: player?.pursuit._id,
				action: ENDLESS_USER_ACTIONS.play_share,		
		}})
		setTimeRemaining(timerLimit)

		sendGtagEvent({
			action: 'shared_playlist_play',
			category: 'engagement',
			label: 'Shared Playlist Play',
			value: 'click',
			playlist_id: player?.pursuit.playlist._id,
			user_id: player.uid,
			topic: player?.pursuit.playlist.topic,
			type: player?.pursuit.type
		});

		sendGtagEvent({
			action: 'leave_share_dialog',
			category: 'engagement',
			label: 'Leave Share Dialog',
			value: 'click',
			user_id: player.uid
		});
	}
	
	if (!infiniteEnabled) {
		return <Error statusCode={404} />
	}

	if (!player?.pursuit && !shareId) {
		return <Page type={playListDetails.key}>
			<UserPrompt onSubmit={onPromptSubmit} onQuit={onCancel} />
		</Page>
	}

	const questionList = player?.pursuit?.playlist?.questions;
	const playerChoices = player?.pursuit?.playerChoices;
	const questionIndex = playerChoices?.length;


			switch (player?.pursuit?.state) {
				case ENDLESS_GAME_STATES.Unavailable:
				case ENDLESS_GAME_STATES.Ready:
					return <Page type={playListDetails.key}>
						<UserPrompt onSubmit={onPromptSubmit} onQuit={onCancel} />
					</Page>
				case ENDLESS_GAME_STATES.Share:
					return <Page type={playListDetails.key}>
						<PlayShareDialog onSharePlaylistPlay={onSharePlaylistPlay} topic={player?.pursuit?.playlist?.topic} type={player?.pursuit?.type} />
					</Page>
				case ENDLESS_GAME_STATES.Question:
					return <Page topAd={true} type={playListDetails.key}>
									<DailyQuestion
										timeTotal={timerLimit}
										timeRemaining={timeRemaining}
										isTimerRunning={clock.isRunning}
										question={questionList[questionIndex]?.question}
										choices={questionList[questionIndex]?.choices}
										category={player?.pursuit.playlist?.topic}
										userChoice={player?.pursuit?.state === ENDLESS_GAME_STATES.Question ? '' : playerChoices[questionIndex-1]} // This has to be determined somehow...
										answer={player?.pursuit?.state === ENDLESS_GAME_STATES.Question ? '' : questionList[questionIndex]?.answer}
										categoryColor={playListDetails.color}
										gameModeTitle={playListDetails.label}
										monotone={true}
										categoryMask={historyToPieMask(questionList, playerChoices)}
										onUserChoice={onUserChoice}
										onNext={onNext}
										secondaryButtonLabel="Quit"
										onQuit={onQuitClick}
										feedback={player?.pursuit?.questionFeedback}
										onFeedback={f => onQuestionFeedback((questionList[questionIndex]?._id), f)}
										isLastQuestion={playerChoices?.length >= 5}
									/>
		
					</Page>
				case ENDLESS_GAME_STATES.Answer:
					return <Page topAd={true}  type={playListDetails.key}>
									<DailyQuestion
										timeTotal={timerLimit}
										timeRemaining={timeRemaining}
										isTimerRunning={clock.isRunning}
										question={questionList[questionIndex-1]?.question}
										choices={questionList[questionIndex-1]?.choices}
										category={player?.pursuit?.playlist?.topic}
										userChoice={player?.pursuit?.state === ENDLESS_GAME_STATES.Question ? '' : playerChoices[questionIndex-1]} // This has to be determined somehow...
										answer={player?.pursuit?.state === ENDLESS_GAME_STATES.Question ? '' : questionList[questionIndex-1]?.answer}
										categoryColor={playListDetails.color}
										gameModeTitle={playListDetails.label}
										monotone={true}
										categoryMask={historyToPieMask(questionList, playerChoices)} 
										onUserChoice={onUserChoice}
										onNext={onNext}
										secondaryButtonLabel="Quit"
										onQuit={onQuitClick}
										feedback={player?.pursuit?.questionFeedback}
										onFeedback={f => onQuestionFeedback((questionList[questionIndex - 1]._id), f)}
										isLastQuestion={playerChoices?.length >= 6}// num of questions per run
									/>
		
					</Page>
				case ENDLESS_GAME_STATES.Summary:
					return <Page topAd={true} type={playListDetails.key}>
						<Summary
							pursuit={player?.pursuit}
							onQuestionFeedback={onQuestionFeedback}
							onPlaylistFeedback={onPlaylistFeedback}
							onPlayAgain={onPlayAgain}
							onShare={onShare}
							onChallenge={onChallenge}
							onQuit={onCancel} />
						</Page>
				case ENDLESS_GAME_STATES.Error:
					return <Page type={playListDetails.key}>
						<ErrorState data={player?.pursuit} onQuit={onQuitClick} onTryAgain={onNext} onStartOver={onResetClick} />
						</Page>
				case ENDLESS_GAME_STATES.Invalid:
					return <Page type={playListDetails.key}>
						<UserPrompt onSubmit={onPromptSubmit} onQuit={onQuit} invalidPrompt={true}/>
					</Page>
				case ENDLESS_GAME_STATES.Setup:
				case ENDLESS_GAME_STATES.None:
				default:
					return <Page type={playListDetails.key}><Loading onCancel={onLoaderCancel} mode={GAME_MODES.endless} /></Page>;
			}



};

export default Infinite;
